import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  DialogProps,
  Grid,
  IconButton,
  Paper,
  Radio,
  Stack as ButtonGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import Label from 'components/Label'
import { usePagination } from 'hooks/pagination.hook'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import teenagerService from 'services/teenager.service'
import { PageableProps } from 'types/pageable.type'
import {
  SummaryTeenagerType,
  teenagerFilterInit,
  teenagerFilterSchema,
  TeenagerFilterType,
} from 'types/search-teenager.type'
import { useAuthContext } from 'contexts/auth.context'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Dialog from '@mui/material/Dialog'
import { GridColDef } from '@mui/x-data-grid'
import FormInput from 'components/FormInput'
import InputDatePicker from 'components/FormInputDatePicker'
import { teenagerRecordFunctionalities } from 'utils/helpers/funcionalidades'
import Button from '@mui/material/Button'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { DataGridCustom } from 'pages/CreateHearing/components/StepIdentification/components/ModalSearchTeenager/styles'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  hearingIdentificationInit,
  HearingIdentificationType,
} from 'types/hearing-identification.type'
import { FormInputSelect, ItemOption } from 'components/FormInputSelect'
import genericService from '../../../../../../services/generic.service'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'
import generalDataService from 'services/general-data.service'
import { HearingGeneralDataType } from 'types/hearing-general-data.type'
import CloseIcon from '@mui/icons-material/Close'
import { AdolescenteIntegraJudicialType } from 'pages/ViewPendingProcessHearing/modal/AdolescentesIntegraJudicialModal'
import client from 'services/client'
import { BaseData } from 'types/base.type'

const SORT_PROP = 'pessoaFisica.nome'

type ModalSearchTeenagerProps = {
  onChangeAdolescente: (value?: HearingIdentificationType) => Promise<void>
}

const ModalSearchTeenager: React.FC<ModalSearchTeenagerProps> = ({ onChangeAdolescente }) => {
  const { id } = useParams()
  const [generalData, setGeneralData] = useState<HearingGeneralDataType>()
  const [open, setOpen] = useState<boolean>(false)
  const [openAlert, setOpenAlert] = useState(false)

  const [fullWidth] = useState(true)
  const [maxWidth] = useState<DialogProps['maxWidth']>('lg')
  const { control, handleSubmit, getValues, reset, setValue } = useForm<TeenagerFilterType>({
    resolver: zodResolver(teenagerFilterSchema),
    defaultValues: teenagerFilterInit,
  })

  const { handleHasPermission } = useAuthContext()
  const {
    page,
    elements: { totalOfElements, handleTotalOfElements },
    rows: { rowsPerPage, rowsOptions },
    handleNewPage,
  } = usePagination()

  const [data, setData] = useState<SummaryTeenagerType[] | undefined>(undefined)
  const [selectionModel, setSelectionModel] = useState<number[]>([])
  const [generoOptions, setGeneroOptions] = useState<ItemOption[]>([])
  const [adolescenteProcesso, setAdolescenteProcesso] =
    useState<AdolescenteIntegraJudicialType | null>(null)

  const handleSubmitForm = useCallback(
    async (filters: TeenagerFilterType): Promise<void> => {
      try {
        if (!filters) return
        const pageable: PageableProps = {
          page,
          pageSize: rowsPerPage,
          sort: SORT_PROP,
        }
        const res = await teenagerService.getByFilters(
          {
            ...filters,
            genero: filters.genero !== '0' ? filters.genero : null,
          },
          pageable,
        )

        setData(res.data.content)
        handleTotalOfElements(res.data.totalElements)
      } catch (error) {
        console.error('Error fetching Teenager:', error)
      }
    },
    [page],
  )

  const fillTeenagerData = async (idPessoaFisica: number): Promise<void> => {
    try {
      if (!idPessoaFisica) return
      const [teenagerDataRes] = await Promise.all([
        teenagerService.getById(idPessoaFisica).then((res) => res.data),
      ])
      if (
        teenagerDataRes.processos?.find(
          (p) => p.numeroCnj === generalData?.numeroCnj && p.id !== Number(id),
        )
      ) {
        setOpenAlert(true)
      } else {
        await handleChangeAdolescente(teenagerDataRes.identAdolescente)
        // setTeenagerPicture(teenagerPictureRes)
      }
    } catch (err) {
      console.error('Teenager Info Requests: ', err)
    }
  }

  const handleChangeAdolescente = async (value?: HearingIdentificationType) => {
    await onChangeAdolescente(value).finally(() => handleClose())
  }

  const handleResetTable = (): void => {
    setData(undefined)
    reset()
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  useEffect(() => {
    ;(async () => {
      const response = await genericService.get('/genero')
      if (response.data) {
        setGeneroOptions(response.data)
      }
    })()
  }, [])

  useEffect(() => {
    if (data && data.length > 0) {
      const filters = getValues()
      handleSubmitForm(filters)
    }
  }, [handleSubmitForm])

  useEffect(() => {
    ;(async () => {
      await generalDataService
        .getById(id)
        .then((response) => response.data && setGeneralData(response.data))
      await client
        .get<BaseData<AdolescenteIntegraJudicialType>>(
          `/integra-policial/processos/adolescentes-oitiva/${id}`,
        )
        .then((response) => response.data && setAdolescenteProcesso(response.data))
    })()
  }, [id])

  useEffect(() => {
    if (adolescenteProcesso && adolescenteProcesso.nomeAdolescente) {
      setValue('nome', adolescenteProcesso.nomeAdolescente)
    }
  }, [adolescenteProcesso])

  const columns: GridColDef[] = [
    {
      field: 'radiobutton',
      headerName: '',
      width: 100,
      sortable: false,
      disableReorder: true,
      disableExport: true,
      disableColumnMenu: true,
      renderCell: (params) => <Radio checked={selectionModel[0] === params.id} value={params.id} />,
    },
    {
      field: 'nome',
      headerName: 'Nome do adolescente',
      minWidth: 300,
      sortable: false,
      disableReorder: true,
      disableExport: true,
      disableColumnMenu: true,
    },
    {
      field: 'dataNascimento',
      headerName: 'Data de nascimento',
      width: 175,
      sortable: false,
      disableReorder: true,
      disableExport: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <span>
          {dayjs(params.value, 'YYYY-MM-DD').isValid()
            ? dayjs(params.value).format('DD/MM/YYYY')
            : 'Não informado.'}
        </span>
      ),
    },
    {
      field: 'genero',
      headerName: 'Gênero',
      width: 175,
      sortable: false,
      disableReorder: true,
      disableExport: true,
      disableColumnMenu: true,
    },
    {
      field: 'acoes',
      headerName: 'Ações',
      width: 100,
      sortable: false,
      disableReorder: true,
      disableExport: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title='Visualizar Adolescente.' placement={'top'}>
          <IconButton
            size='small'
            onClick={() => {
              window.open(`/ficha-adolescente/${params.id}`, '_blank', 'rel=noopener noreferrer')
            }}
          >
            <VisibilityIcon fontSize='inherit' />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <Box>
      <Button
        variant='contained'
        color='primary'
        size={'medium'}
        startIcon={<SearchRoundedIcon />}
        sx={{
          marginLeft: '0.5rem',
        }}
        onClick={handleOpen}
      >
        Buscar
      </Button>
      <Dialog onClose={handleClose} open={open} fullWidth={fullWidth} maxWidth={maxWidth}>
        <DialogTitle>
          <Typography variant={'h6'} color={'primary.main'}>
            Buscar Adolescente
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Grid container direction='column' spacing={4}>
            <Grid item xs={12} sx={{ pb: 2 }}>
              <form>
                <Grid container spacing={2} rowSpacing={2}>
                  <Grid item md={6} sm={5} xs={12}>
                    <FormInput
                      name='nome'
                      control={control}
                      label='Nome do adolescente'
                      placeholder='Informe o nome do adolescente'
                      maxLength={60}
                    />
                  </Grid>
                  <Grid item md={6} sm={5} xs={12}>
                    <FormInput
                      name='nomeSocial'
                      control={control}
                      label='Nome social'
                      placeholder='Informe o nome social do adolescente'
                      maxLength={60}
                    />
                  </Grid>
                  <Grid item md={3} sm={3} xs={12}>
                    <InputDatePicker
                      name='dataNascimento'
                      control={control}
                      label='Data de nascimento'
                    />
                  </Grid>
                  <Grid item md={3} sm={4} xs={12}>
                    <FormInputSelect
                      label={'Gênero'}
                      name={'genero'}
                      control={control}
                      options={generoOptions}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <FormInput
                      name='nomeMae'
                      control={control}
                      label='Nome da mãe'
                      placeholder='Informe o nome da mãe do adolescente'
                    />
                  </Grid>
                  {handleHasPermission(teenagerRecordFunctionalities.consultar) && (
                    <Grid item xs={12}>
                      <ButtonGroup direction='row' spacing={2} sx={{ mt: 1 }}>
                        <Button variant='outlined' color='secondary' onClick={handleResetTable}>
                          Limpar
                        </Button>
                        <Button
                          variant='contained'
                          color='secondary'
                          startIcon={<SearchRoundedIcon />}
                          onClick={() => {
                            handleSubmit(async (data) => {
                              handleNewPage(null, 0)
                              await handleSubmitForm(data)
                            })()
                          }}
                        >
                          Buscar
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  )}
                </Grid>
              </form>
            </Grid>

            {data && (
              <Grid item xs={12}>
                {data.length > 0 ? (
                  <Stack direction='column' spacing={2}>
                    <Label text='Resultados da busca:' />
                    <Paper variant='outlined'>
                      <DataGridCustom
                        rows={data}
                        columns={columns}
                        autoHeight
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={(newSelectionModel: any) => {
                          setSelectionModel(newSelectionModel)
                        }}
                        pageSizeOptions={rowsOptions}
                        localeText={{
                          footerRowSelected: (numero) =>
                            numero > 1 ? `${numero} selecionados` : `${numero} selecionado.`,
                        }}
                        rowCount={totalOfElements}
                        paginationMode='server'
                        componentsProps={{
                          pagination: {
                            labelRowsPerPage: 'Linhas por página:',
                            labelDisplayedRows: ({ from, to, count }) =>
                              `Linhas ${from}-${to} de um total de ${count} itens.`,
                          },
                        }}
                        onPaginationModelChange={({ page }) => {
                          handleNewPage(null, page)
                        }}
                        paginationModel={{
                          page: page,
                          pageSize: rowsPerPage,
                        }}
                      />
                    </Paper>
                  </Stack>
                ) : (
                  <Stack direction='row' justifyContent='center' spacing={2}>
                    <Label text='Nenhum resultado encontrado...' bold />
                  </Stack>
                )}
              </Grid>
            )}

            <Grid item xs={12} display={'flex'} justifyContent={'center'}>
              <Button
                variant='contained'
                color='info'
                onClick={async () => {
                  await handleChangeAdolescente({
                    ...hearingIdentificationInit,
                    nome: adolescenteProcesso?.nomeAdolescente || '',
                  })
                }}
              >
                Adolescente Não encontrado
              </Button>
              <Button
                variant='contained'
                color='secondary'
                disabled={!(selectionModel.length > 0)}
                onClick={async () => {
                  await fillTeenagerData(selectionModel[0])
                }}
                sx={{
                  marginLeft: '0.5rem',
                }}
              >
                Avançar
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog onClose={() => setOpenAlert(false)} open={openAlert}>
        <DialogTitle>
          <Typography variant={'h6'}>
            Este Adolescente já possui uma Oitiva neste processo.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container direction='column' spacing={4}>
            <Grid item xs={12} display={'flex'} justifyContent={'center'}>
              <Button
                variant='contained'
                color='info'
                onClick={async () => {
                  await handleChangeAdolescente().finally(() => setOpenAlert(false))
                }}
              >
                Fechar
              </Button>
              <Button
                variant='contained'
                color='secondary'
                disabled={!(selectionModel.length > 0)}
                onClick={async () => {
                  setOpenAlert(false)
                }}
                sx={{
                  marginLeft: '0.5rem',
                }}
              >
                Realizar Nova Busca
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default ModalSearchTeenager
